import React from 'react';
import RefLink from '../shared/RefLink';
import { engagementDisplayName } from '../../utils/engagementName';

/**
 * Why the record / engagement just picked cannot take a new request — said
 * ON the page, under the picker, instead of the option silently missing from
 * the list (a missing option only produces "why is my record not listed?").
 * Every restriction the server refuses at POST is named here first
 * (docs/workflow-ui.md §4.2, contract §2d / §2e / §2g):
 *
 *   engagementLock  { locked, engagements }  — a covered engagement is closed
 *                                              (409 ENGAGEMENT_CLOSED)
 *   openRequests    { qca, qcad, others }    — an amendment / addendum in
 *                                              flight on the QC record (409
 *                                              TYPE_ALREADY_PENDING /
 *                                              AMENDMENT_PENDING /
 *                                              ADDENDUM_PENDING), and
 *                                              `others` = another flow's
 *                                              request in review on a covered
 *                                              engagement (409
 *                                              OPEN_REQUESTS_PENDING — one
 *                                              request at a time per
 *                                              engagement)
 *
 * `flow` is the request being written; the same-type open request reads
 * "modify that one", any other "wait for it".
 *
 * Engagement pickers use the second half: every options row carries
 * `openRequests: [{ flow, id, requestNo }]` (engagementLock.attachOpenRequests)
 * and `EngagementBlockers` prints the ones on the selected rows.
 */
export const FLOW_LABEL = {
  qc: 'QC6 / QC7 Forms', fs: 'Signed Financial Statements', qc35: 'QC35 Form',
  ear: 'Engagement Amendment', esc: 'Engagement Status Change',
  qca: 'QC Amendment', qcad: 'QC6 / QC7 Addendum',
  fsa: 'Signed Financial Statements Amendment', qc35a: 'QC35 Form Amendment',
  cwcc: 'CaseWare, Charge Code & Team Update',
};

const otherFlowReason = (open, flow, where) => ({
  key: `${open.flow}-${open.id}`, request: open, path: `/workflow/${open.flow}/${open.id}`,
  text: `${FLOW_LABEL[open.flow] || open.flow} ${open.requestNo} is still in review${where ? ` on ${where}` : ''} — one request at a time per engagement. Wait until it is approved, returned or deleted before raising a ${FLOW_LABEL[flow] || 'request'}.`,
});

export const recordBlockReasons = ({ engagementLock, openRequests, flow }) => {
  const reasons = [];
  if (engagementLock && engagementLock.locked) {
    const names = (engagementLock.engagements || []).map((e) => `${engagementDisplayName(e.engagementName, e.serviceDescription)} (${e.status})`).join(', ');
    reasons.push({ key: 'closed', text: `A covered engagement is no longer in progress — ${names}. Management can set it back to Active if this ${FLOW_LABEL[flow] || 'request'} is still needed.` });
  }
  for (const other of ['qca', 'qcad']) {
    const open = openRequests && openRequests[other];
    if (!open) continue;
    reasons.push({
      key: other, request: open, path: `/workflow/${other}/${open.id}`,
      text: other === flow
        ? `A ${FLOW_LABEL[other]} (${open.requestNo}) is already open on this record — modify that request instead.`
        : `A ${FLOW_LABEL[other]} (${open.requestNo}) is open on this record — wait until it is approved or withdrawn before raising a ${FLOW_LABEL[flow]}.`,
    });
  }
  for (const open of (openRequests && openRequests.others) || []) {
    const where = (open.engagements || []).filter((e) => e.engagementName)
      .map((e) => engagementDisplayName(e.engagementName, e.serviceDescription)).join(', ');
    reasons.push(otherFlowReason(open, flow, where));
  }
  return reasons;
};

/**
 * Engagement pickers: the other-flow requests in review on the SELECTED
 * rows (each row's `openRequests`), one reason per request. Empty ⇒ the
 * selection can be submitted as far as this rule goes.
 */
export const engagementBlockReasons = (selected, flow) => {
  const seen = new Set();
  const reasons = [];
  for (const en of selected || []) {
    for (const open of (en && en.openRequests) || []) {
      const key = `${open.flow}-${open.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      reasons.push(otherFlowReason(open, flow, engagementDisplayName(en.engagementName, en.serviceDescription)));
    }
  }
  return reasons;
};

const Box = ({ title, reasons }) => (
  <div className="mt-2 border border-amber-200 rounded-lg p-3 bg-amber-50 text-sm text-amber-800 space-y-1">
    <p className="font-medium">{title}</p>
    {reasons.map((r) => (
      <p key={r.key}>
        {r.text}
        {r.request && (
          <>
            {' '}
            <RefLink to={r.path} className="font-medium underline hover:text-amber-900">Open {r.request.requestNo}</RefLink>
          </>
        )}
      </p>
    ))}
  </div>
);

const RecordBlockers = ({ engagementLock, openRequests, flow }) => {
  const reasons = recordBlockReasons({ engagementLock, openRequests, flow });
  if (reasons.length === 0) return null;
  return <Box title={`This record cannot receive a ${FLOW_LABEL[flow] || 'request'} right now.`} reasons={reasons} />;
};

/**
 * Under an engagement picker: the selected engagement(s) already have
 * another flow's request in review. `note` adds a line the flow wants said
 * (the QC form: a draft can still be saved).
 */
export const EngagementBlockers = ({ selected, flow, note = null }) => {
  const reasons = engagementBlockReasons(selected, flow);
  if (reasons.length === 0) return null;
  return (
    <Box
      title={`This engagement cannot receive a ${FLOW_LABEL[flow] || 'request'} right now.${note ? ` ${note}` : ''}`}
      reasons={reasons}
    />
  );
};

export default RecordBlockers;
